import { useEffect, useState } from 'react';
import { fetchPromptConfig, fetchRestaurant, fetchRestaurantById } from '../services/api.ts';
import { ATENDABOT_PROMPT_STATUS_EVENT } from '../utils/promptStatus.ts';

const readCachedRestaurant = (cacheKey: string) => {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed || null;
  } catch (_) {
    return null;
  }
};

const hasInstanceTokenValue = (obj: any) =>
  obj?.whatsapp_instance_configured === true
  || !!`${obj?.atendabot_instance_token ?? ''}`.trim().length;

type UseAtendabotRestaurantOptions = {
  restauranteId?: string | number | null;
  isAdmin?: boolean;
};

export function useAtendabotRestaurant({
  restauranteId,
  isAdmin,
}: UseAtendabotRestaurantOptions = {}) {
  const restaurantCacheKey = restauranteId
    ? `atendabot-restaurant-${restauranteId}`
    : 'atendabot-restaurant';

  const initialRestaurante = readCachedRestaurant(restaurantCacheKey);

  const [restaurante, setRestaurante] = useState<any>(initialRestaurante);
  const [loadingRestaurant, setLoadingRestaurant] = useState(true);
  const [promptSourceResolved, setPromptSourceResolved] = useState(Boolean(initialRestaurante));
  const [promptDraftPending, setPromptDraftPending] = useState(false);
  const [promptReadyToPublish, setPromptReadyToPublish] = useState(true);

  useEffect(() => {
    const cachedRestaurant = readCachedRestaurant(restaurantCacheKey);
    if (!cachedRestaurant) setPromptSourceResolved(false);
    const loadRestaurant = async () => {
      setLoadingRestaurant(true);
      try {
        let data = await fetchRestaurant(restauranteId);

        const hasInstanceTokenFromConfig = hasInstanceTokenValue(data);
        if (!hasInstanceTokenFromConfig && restauranteId && isAdmin) {
          try {
            const byId = await fetchRestaurantById(restauranteId);
            data = { ...byId, ...data };
          } catch (fallbackError) {
            console.warn('Erro ao buscar restaurante por ID para validar Atendabot:', fallbackError);
          }
        }

        setRestaurante(data);
        setPromptSourceResolved(true);
        sessionStorage.setItem(restaurantCacheKey, JSON.stringify(data));
      } catch (error) {
        console.error('Error fetching restaurant data for Atendabot:', error);
      } finally {
        setLoadingRestaurant(false);
      }
    };

    if (cachedRestaurant) {
      setRestaurante(cachedRestaurant);
      setPromptSourceResolved(true);
    }

    loadRestaurant();
  }, [restaurantCacheKey, isAdmin, restauranteId]);

  const hasInstanceToken = Boolean(
    restaurante?.whatsapp_instance_configured === true
    || (restaurante?.atendabot_instance_token || '').toString().trim()
  );
  const usesManagerPrompt = restaurante?.atendabot_usar_prompt_manager === true;

  useEffect(() => {
    if (!promptSourceResolved || usesManagerPrompt) {
      setPromptDraftPending(false);
      setPromptReadyToPublish(true);
      return;
    }
    let cancelled = false;
    const loadPromptStatus = async () => {
      try {
        const data: any = await fetchPromptConfig();
        if (cancelled) return;
        setPromptDraftPending(Boolean(data?.draft_config));
        setPromptReadyToPublish(Boolean(data?.prompt_ready_to_publish));
      } catch (error) {
        console.error('Erro ao verificar status do atendimento com IA:', error);
      }
    };
    loadPromptStatus();
    return () => {
      cancelled = true;
    };
  }, [promptSourceResolved, usesManagerPrompt, restauranteId]);

  useEffect(() => {
    const updatePromptStatus = (event: Event) => {
      const detail = (event as CustomEvent)?.detail || {};
      if (typeof detail.draftPending === 'boolean') {
        setPromptDraftPending(detail.draftPending);
      }
      if (typeof detail.promptReadyToPublish === 'boolean') {
        setPromptReadyToPublish(detail.promptReadyToPublish);
      }
    };
    window.addEventListener(ATENDABOT_PROMPT_STATUS_EVENT, updatePromptStatus);
    return () => window.removeEventListener(ATENDABOT_PROMPT_STATUS_EVENT, updatePromptStatus);
  }, []);

  return {
    restaurante,
    setRestaurante,
    restaurantCacheKey,
    loadingRestaurant,
    promptSourceResolved,
    promptDraftPending,
    promptReadyToPublish,
    hasInstanceToken,
    usesManagerPrompt,
  };
}
